import { useState } from 'react';
import {
    Table,
    Button,
    Typography,
    Space,
    Tag,
    Modal,
    Form,
    Input,
    Switch,
    message,
    Popconfirm,
    Drawer,
    Avatar,
    List,
    Tooltip,
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    UserOutlined,
    StopOutlined,
    CheckCircleOutlined,
    AppstoreOutlined,
    CommentOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
    useTenants,
    useTenantUsers,
    useCreateTenant,
    useUpdateTenant,
    useDeleteTenant,
    useBlockTenant,
} from '@/hooks/useSystemAdminApi';
import type { TenantUser } from '@/types/admin';
import AddUserModal from '@/pages/user-management/add_user_modal';
import { Tenant } from 'breuer-supercharged-sdk';
import TenantModulesDrawer from './TenantModulesDrawer';
import TenantFeedbackReviewDrawer from './TenantFeedbackReviewDrawer';
import { enabledModuleLabels } from './tenant_modules';

const { Title, Text } = Typography;

export default function TenantManagement() {
    const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isUserDrawerVisible, setIsUserDrawerVisible] = useState(false);
    const [selectedTenantId, setSelectedTenantId] = useState<string | null>(null);
    const [selectedTenantName, setSelectedTenantName] = useState('');
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
    const [modulesTenant, setModulesTenant] = useState<Tenant | null>(null);
    const [isModulesDrawerOpen, setIsModulesDrawerOpen] = useState(false);
    const [modulesDrawerSession, setModulesDrawerSession] = useState(0);
    const [reviewTenant, setReviewTenant] = useState<Tenant | null>(null);
    const [isReviewDrawerOpen, setIsReviewDrawerOpen] = useState(false);
    const [reviewDrawerSession, setReviewDrawerSession] = useState(0);
    const [form] = Form.useForm();

    // React Query hooks
    const { data: tenants = [], isLoading: loading } = useTenants();
    const { data: selectedTenantUsers = [] } = useTenantUsers(selectedTenantId);
    const createTenantMutation = useCreateTenant();
    const updateTenantMutation = useUpdateTenant();
    const deleteTenantMutation = useDeleteTenant();
    const blockTenantMutation = useBlockTenant();


    const loadTenantUsers = (tenantId: string, tenantName: string) => {
        setSelectedTenantId(tenantId);
        setSelectedTenantName(tenantName);
        setIsUserDrawerVisible(true);
    };

    const handleCreateTenant = () => {
        setEditingTenant(null);
        setIsModalVisible(true);
        form.resetFields();
    };

    const handleEditTenant = (tenant: Tenant) => {
        setEditingTenant(tenant);
        setIsModalVisible(true);
        form.setFieldsValue(tenant);
    };

    const handleEditFeedbackReview = (tenant: Tenant) => {
        setReviewTenant(tenant);
        setReviewDrawerSession((n) => n + 1);
        setIsReviewDrawerOpen(true);
    };

    const handleEditModules = (tenant: Tenant) => {
        setModulesTenant(tenant);
        setModulesDrawerSession((n) => n + 1);
        setIsModulesDrawerOpen(true);
    };

    const handleDeleteTenant = async (tenantId: string) => {
        try {
            await deleteTenantMutation.mutateAsync(tenantId);
            message.success('Mandant erfolgreich gelöscht');
        } catch (error) {
            console.error('Error deleting tenant:', error);
            message.error('Fehler beim Löschen des Mandanten');
        }
    };

    const handleBlockTenant = async (tenantId: string, isActive: boolean) => {
        try {
            await blockTenantMutation.mutateAsync({ tenantId, isBlocked: !isActive });
            message.success(
                isActive ? 'Mandant erfolgreich blockiert' : 'Mandant erfolgreich entsperrt'
            );
        } catch (error) {
            console.error('Error updating tenant status:', error);
            message.error('Fehler beim Aktualisieren des Mandantenstatus');
        }
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();

            if (editingTenant) {
                await updateTenantMutation.mutateAsync({ tenantId: editingTenant.id, tenantData: values });
                message.success('Mandant erfolgreich aktualisiert');
            } else {
                await createTenantMutation.mutateAsync(values);
                message.success('Mandant erfolgreich erstellt');
            }

            setIsModalVisible(false);
        } catch (error) {
            console.error('Error saving tenant:', error);
            message.error('Fehler beim Speichern des Mandanten');
        }
    };

    const columns: ColumnsType<Tenant> = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            fixed: 'left',
            render: (text: string) => <Text strong>{text}</Text>,
        },
        {
            title: 'Slug',
            dataIndex: 'slug',
            key: 'slug',
            render: (text: string) => <Text code>{text}</Text>,
        },
        {
            title: 'Status',
            dataIndex: 'isActive',
            key: 'isActive',
            render: (isActive: boolean) => (
                <Tag color={isActive ? 'success' : 'error'}>
                    {isActive ? 'Aktiv' : 'Blockiert'}
                </Tag>
            ),
        },
        {
            title: 'Benutzer',
            dataIndex: 'userCount',
            key: 'userCount',
            render: (count: number, record: Tenant) => (
                <Button
                    type="link"
                    onClick={() => loadTenantUsers(record.id, record.name)}
                    style={{ padding: 0 }}
                >
                    {count} Benutzer
                </Button>
            ),
        },
        {
            title: 'Module',
            dataIndex: 'features',
            key: 'features',
            render: (_, record: Tenant) => {
                const labels = enabledModuleLabels(record.features);
                if (labels.length === 0) return <Text type="secondary">Keine</Text>;
                return (
                    <Space size={[4, 4]} wrap style={{ maxWidth: 240 }}>
                        {labels.map((label) => (
                            <Tag key={label} color="blue" style={{ marginInlineEnd: 0 }}>
                                {label}
                            </Tag>
                        ))}
                    </Space>
                );
            },
        },
        {
            title: 'Kontakt',
            dataIndex: 'contactEmail',
            key: 'contactEmail',
        },
        {
            title: 'Erstellt',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date: string) => new Date(date).toLocaleDateString('de-DE'),
        },
        {
            title: 'Aktionen',
            key: 'actions',
            fixed: 'right',
            render: (_, record: Tenant) => (
                <Space size="small">
                    <Tooltip title="Module">
                        <Button
                            type="default"
                            shape="circle"
                            icon={<AppstoreOutlined />}
                            aria-label={`Module von ${record.name}`}
                            onClick={() => handleEditModules(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Feedback-Prüfung">
                        <Button
                            type="default"
                            shape="circle"
                            icon={<CommentOutlined />}
                            aria-label={`Feedback-Prüfung von ${record.name}`}
                            onClick={() => handleEditFeedbackReview(record)}
                        />
                    </Tooltip>
                    <Tooltip title="Bearbeiten">
                        <Button
                            type="default"
                            shape="circle"
                            icon={<EditOutlined />}
                            onClick={() => handleEditTenant(record)}
                        />
                    </Tooltip>
                    <Tooltip title={record.isActive ? 'Blockieren' : 'Entsperren'}>
                        <Button
                            type="default"
                            shape="circle"
                            danger={record.isActive}
                            icon={record.isActive ? <StopOutlined /> : <CheckCircleOutlined />}
                            onClick={() => handleBlockTenant(record.id, record.isActive)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Mandant löschen"
                        description="Sind Sie sicher, dass Sie diesen Mandanten löschen möchten?"
                        onConfirm={() => handleDeleteTenant(record.id)}
                        okText="Ja"
                        cancelText="Nein"
                        okButtonProps={{ danger: true }}
                    >
                        <Tooltip title="Löschen">
                            <Button
                                type="default"
                                shape="circle"
                                danger
                                icon={<DeleteOutlined />}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Title level={2}>Mandanten Verwaltung</Title>
                    <Text type="secondary">
                        Verwalten Sie alle Mandanten im System
                    </Text>
                </div>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleCreateTenant}
                    size="large"
                >
                    Neuen Mandanten erstellen
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={tenants}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
                scroll={{ x: 'max-content' }}
            />

            <Modal
                title={editingTenant ? 'Mandant bearbeiten' : 'Neuen Mandanten erstellen'}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={() => setIsModalVisible(false)}
                okText={editingTenant ? 'Aktualisieren' : 'Erstellen'}
                cancelText="Abbrechen"
                width={800}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="name"
                        label="Name"
                        rules={[{ required: true, message: 'Name ist erforderlich' }]}
                    >
                        <Input placeholder="Mandantenname" />
                    </Form.Item>
                    <Form.Item
                        name="slug"
                        label="Slug (URL-Kennung)"
                        rules={[{ required: true, message: 'Slug ist erforderlich' }]}
                    >
                        <Input placeholder="mandant-slug" />
                    </Form.Item>
                    <Form.Item
                        name="contactEmail"
                        label="Kontakt E-Mail"
                        rules={[{ type: 'email', message: 'Ungültige E-Mail-Adresse' }]}
                    >
                        <Input placeholder="kontakt@example.com" />
                    </Form.Item>
                    <Form.Item
                        name="billingEmail"
                        label="Abrechnungs E-Mail"
                        rules={[{ type: 'email', message: 'Ungültige E-Mail-Adresse' }]}
                    >
                        <Input placeholder="abrechnung@example.com" />
                    </Form.Item>
                    <Form.Item name="address" label="Adresse">
                        <Input placeholder="Straße und Hausnummer" />
                    </Form.Item>
                    <Form.Item name="city" label="Stadt">
                        <Input placeholder="Stadt" />
                    </Form.Item>
                    <Form.Item name="zip" label="Postleitzahl">
                        <Input placeholder="PLZ" />
                    </Form.Item>
                    <Form.Item name="country" label="Land">
                        <Input placeholder="Land" />
                    </Form.Item>
                    <Form.Item name="isActive" label="Aktiv" valuePropName="checked">
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>

            <Drawer
                title={`Benutzer von ${selectedTenantName}`}
                open={isUserDrawerVisible}
                onClose={() => {
                    setIsUserDrawerVisible(false);
                    setSelectedTenantId(null);
                }}
                width={400}
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setIsAddUserModalOpen(true)}
                    >
                        Benutzer hinzufügen
                    </Button>
                }
            >
                <List
                    dataSource={selectedTenantUsers}
                    renderItem={(user: TenantUser) => (
                        <List.Item>
                            <List.Item.Meta
                                avatar={<Avatar icon={<UserOutlined />} />}
                                title={user.displayName}
                                description={user.email}
                            />
                        </List.Item>
                    )}
                />
            </Drawer>

            {modulesTenant && (
                <TenantModulesDrawer
                    key={modulesDrawerSession}
                    tenant={modulesTenant}
                    open={isModulesDrawerOpen}
                    onClose={() => setIsModulesDrawerOpen(false)}
                />
            )}

            {reviewTenant && (
                <TenantFeedbackReviewDrawer
                    key={reviewDrawerSession}
                    tenant={reviewTenant}
                    open={isReviewDrawerOpen}
                    onClose={() => setIsReviewDrawerOpen(false)}
                />
            )}

            {selectedTenantId && (
                <AddUserModal
                    open={isAddUserModalOpen}
                    onClose={() => setIsAddUserModalOpen(false)}
                    tenantId={selectedTenantId}
                    tenantName={selectedTenantName}
                />
            )}
        </div>
    );
}